"use client";

import React, { useEffect, useRef, useState } from 'react';
import dynamic from 'next/dynamic';
import { useReducedMotion } from '@/hooks/use-reduced-motion';
import { DOT_GRID_BACKGROUND } from '@/lib/dot-grid';

/**
 * Light mode's background: a mild grid of dots joined by fine lines that move
 * out of the mouse's way (owner, 2026-10-23). ClientLayout mounts it when the
 * gate (src/lib/cosmic-effects.ts) says so: light mode, the viewer's switch
 * on, a platform page, never checkout.
 *
 * The still grid is a CSS background (src/lib/dot-grid.ts) on a layer as tall
 * as the document, in the root stacking context under the content, the same
 * place the dark-mode gradient takes. The part that moves is fetched only
 * once a mouse has moved over the page, and never for a visitor who asked for
 * reduced motion: a phone, a tablet or a still page never downloads it, and
 * the grid there is exactly the CSS one.
 */

const DotGridRipple = dynamic(() => import('@/components/ui/dot-grid-ripple'), { ssr: false });

/** True from the first time a mouse (not a finger or a pen) moves over the page. */
function useMouseSeen(): boolean {
    const [seen, setSeen] = useState(false);
    useEffect(() => {
        if (seen) return;
        const onMove = (event: PointerEvent) => {
            if (event.pointerType === 'mouse') setSeen(true);
        };
        window.addEventListener('pointermove', onMove, { passive: true });
        return () => window.removeEventListener('pointermove', onMove);
    }, [seen]);
    return seen;
}

export function DotGrid() {
    const layer = useRef<HTMLDivElement | null>(null);
    const reduceMotion = useReducedMotion();
    const mouse = useMouseSeen();
    return (
        <div
            ref={layer}
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 -z-20 overflow-hidden animate-in fade-in duration-700 motion-reduce:animate-none print:hidden"
            style={DOT_GRID_BACKGROUND}
        >
            {mouse && !reduceMotion && <DotGridRipple layer={layer} />}
        </div>
    );
}
